import { colors } from "../../styles/colors";

const hexToRgb = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

const lerpColor = (from: string, to: string, t: number): string => {
  const [r1, g1, b1] = hexToRgb(from);
  const [r2, g2, b2] = hexToRgb(to);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `rgb(${mix(r1, r2)}, ${mix(g1, g2)}, ${mix(b1, b2)})`;
};

/** Zweifarbiger Verlauf für t in [0, 1]. */
const lerp2 = (from: string, to: string, t: number): string =>
  lerpColor(from, to, Math.max(0, Math.min(1, t)));

/** Dreifarbiger Verlauf für t in [0, 1] (z.B. Rot → Gelb → Grün). */
const lerp3 = (from: string, mid: string, to: string, t: number): string => {
  const clamped = Math.max(0, Math.min(1, t));
  return clamped <= 0.5
    ? lerpColor(from, mid, clamped * 2)
    : lerpColor(mid, to, (clamped - 0.5) * 2);
};

/**
 * Chip-Farben für den Reifegrad-Score: stufenloser Verlauf innerhalb der
 * Teal-Familie, niedrige Werte hell, hohe Werte kräftig/dunkel (Hintergrund
 * und Schrift gleichermaßen).
 */
export function getReifegradChipColor(value: number): {
  background: string;
  main: string;
} {
  const t = value / 100;
  return {
    background: lerp2(colors.brand.teal50, colors.brand.teal300, t),
    main: lerp2(colors.brand.teal400, colors.brand.teal900, t),
  };
}

/**
 * Ampelfarbe für eine einzelne Reifegrad-Domäne (0-100), z.B. für die
 * Segmente im Reifegrad-Breakdown-Tooltip. Anders als der stufenlose
 * Teal-Verlauf des Gesamt-Chips: feste Rot/Gelb/Grün-Schwellen, damit
 * Problem-Domänen ohne Nachdenken erkennbar sind. Bewusst helle
 * Pastelltöne (`colors.reifegradSegment`) statt der kräftigen
 * `colors.status`-Farben, damit der Tooltip freundlich statt alarmierend
 * wirkt. `null` (Domäne noch nicht erfasst/relevant) wird grau dargestellt.
 */
export function getReifegradSegmentColor(value: number | null): string {
  if (value === null) {
    return colors.reifegradSegment.missing;
  }
  if (value < 25) {
    return colors.reifegradSegment.error;
  }
  if (value < 75) {
    return colors.reifegradSegment.warning;
  }
  return colors.reifegradSegment.success;
}

/**
 * Chip-Farben für den Wirtschaftlichkeits-Score: stufenloser Verlauf Rot →
 * Orange → Grün, damit auch benachbarte Werte (z.B. 5,0 und 6,0) sich
 * sichtbar unterscheiden statt in dieselbe Schwelle zu fallen. Zusätzlich
 * zum Farbton wird - analog zum Reifegrad - auch die Helligkeit skaliert:
 * schlechte Werte hell, gute Werte kräftig/dunkel.
 */
export function getWirtschaftlichkeitChipColor(value: number): {
  background: string;
  main: string;
} {
  const t = value / 100;
  const { wirtschaftlichkeit } = colors.scoreGradient;
  return {
    background: lerp3(
      wirtschaftlichkeit.backgroundLow,
      wirtschaftlichkeit.backgroundMid,
      wirtschaftlichkeit.backgroundHigh,
      t,
    ),
    main: lerp3(
      wirtschaftlichkeit.textLow,
      wirtschaftlichkeit.textMid,
      wirtschaftlichkeit.textHigh,
      t,
    ),
  };
}
